/**
 * Zitierte Verläufe in einer Mail abtrennen: Wie Outlook iOS bleibt der ganze
 * Verlauf sichtbar, eine dünne Linie trennt ihn vom neuen Text. Rein über das
 * DOM des Mail-iframes (der iframe läuft ohne Skripte).
 *
 * Erkannt wird der BEGINN des Zitats. Bewusst nur eindeutige Marker der großen
 * Clients — lieber keine Linie als eine mitten im eigenen Text.
 */
export const ZITAT_SELEKTOREN = [
    // Outlook (Web/neu/iOS)
    "#divRplyFwdMsg",
    "#appendonsend",
    "#mail-editor-reference-message-container",
    "div.OutlookMessageHeader",
    // Outlook Desktop: Trennlinie über „Von: … Gesendet: …"
    'div[style*="border-top:solid #E1E1E1" i]',
    'div[style*="border-top: solid #E1E1E1" i]',
    'div[style*="border-top:solid #B5C4DF" i]',
    'div[style*="border-top: solid #B5C4DF" i]',
    // Gmail, Apple Mail, Thunderbird
    "div.gmail_quote",
    'blockquote[type="cite"]',
    "div.moz-cite-prefix",
] as const;

export const ZITAT_TRENNER_ID = "kmvs-zitat-trenner";

/** Das erste Element, mit dem ein zitierter Verlauf beginnt (in Dokumentreihenfolge). */
export function findeZitatBeginn(doc: Document): Element | null {
    let erster: Element | null = null;
    for (const sel of ZITAT_SELEKTOREN) {
        let el: Element | null = null;
        try {
            el = doc.body?.querySelector(sel) ?? null;
        } catch {
            el = null; // Selektor vom Browser nicht unterstützt (z. B. Flag `i`)
        }
        if (!el) continue;
        if (!erster || erster.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) erster = el;
    }
    // Steht davor gar kein eigener Text, ist das „Zitat" die ganze Mail — dann keine Linie.
    if (!erster || !doc.body) return null;
    const bereich = doc.createRange();
    bereich.setStart(doc.body, 0);
    bereich.setEndBefore(erster);
    if (!bereich.toString().trim()) return null;
    return erster;
}

/** Zieht der Absender selbst schon eine Linie über das Zitat (Outlook: `<hr>` bzw. `border-top`)? */
function hatEigeneLinie(beginn: Element): boolean {
    if (/border-top\s*:\s*solid/i.test(beginn.getAttribute("style") ?? "")) return true;
    let vor = beginn.previousElementSibling;
    while (vor && !vor.textContent?.trim() && vor.tagName !== "HR" && !vor.querySelector("hr")) vor = vor.previousElementSibling;
    return !!vor && (vor.tagName === "HR" || (!vor.textContent?.trim() && !!vor.querySelector("hr")));
}

/**
 * Setzt vor den Beginn des Zitats eine dünne Trennlinie (Outlook iOS); der
 * Verlauf bleibt vollständig sichtbar. Zieht die Mail selbst schon eine Linie,
 * kommt keine zweite dazu. Liefert `false`, wenn es kein Zitat gibt.
 */
export function zitatAbtrennen(doc: Document): boolean {
    if (doc.getElementById(ZITAT_TRENNER_ID)) return true;
    const beginn = findeZitatBeginn(doc);
    if (!beginn) return false;
    if (hatEigeneLinie(beginn)) return true;
    const linie = doc.createElement("div");
    linie.id = ZITAT_TRENNER_ID;
    linie.setAttribute("role", "separator");
    linie.setAttribute("style", "border-top:1px solid #e1dfe3;margin:16px 0;height:0");
    beginn.parentElement?.insertBefore(linie, beginn);
    return true;
}
